import { useQuery } from '@apollo/client';
import { faBarsFilter, faEllipsisVertical, faRobot, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import Button from '../../../core/components/Button.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import EmptyStage from '../../../core/components/EmptyStage.tsx';
import Filter from '../../../core/components/Filter.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Page from '../../../core/components/Page';
import SearchField from '../../../core/components/SearchField.tsx';
import Table, { TableScrollWrapper } from '../../../core/components/Table.tsx';
import TablePagination from '../../../core/components/TablePagination.tsx';
import { SAD } from '../../../core/components/worki/Pose.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { useDebounce } from '../../../core/utils/hooks/useDebounce.ts';
import { url } from '../../../core/utils/link.ts';
import useSearchParamState, {
    useArraySearchParamState,
    useBooleanSearchParamState,
} from '../../../core/utils/useSearchParamState.tsx';
import CreateCustomCategoryModal, {
    CategoryFormData,
    CATEGORY_FORM_DEFAULT_VALUES,
} from '../../../customer/pages/SmartInbox/CreateCustomCategoryModal.tsx';
import { RecommendedCategory } from '../../../customer/pages/SmartInbox/RecommendedCategoriesGrid.tsx';
import { GET_SMART_INBOX_INTEGRATION } from '../../../customer/queries.ts';
import { CATEGORY_CATALOG_DETAIL_PATH } from '../constants.ts';
import useCategoryCatalog, { CategoryCatalogRow } from '../hooks/useCategoryCatalog.ts';
import useCategoryCreate from '../hooks/useCategoryCreate.ts';
import useCategoryOperations from '../hooks/useCategoryOperations.ts';

const AgentIcon = withIcon(faRobot);
const DotIcon = withIcon(faCircle);
const FilterListIcon = withIcon(faBarsFilter);
const EllipsisIcon = withIcon(faEllipsisVertical);
const SpinnerIcon = withIcon(faSpinnerThird);

const DEFAULT_PAGE_SIZE = 10;

interface CategoryCatalogTabProps {
    tabsNav: React.ReactNode;
    onEmptyChange?: (isEmpty: boolean) => void;
}

export interface CategoryCatalogTabHandle {
    openCreateModal: () => void;
}

const CategoryCatalogTab = React.forwardRef<CategoryCatalogTabHandle, CategoryCatalogTabProps>(
    function CategoryCatalogTab({ tabsNav, onEmptyChange }, ref) {
        const { t } = useTranslation('mailboxes');
        const navigate = useNavigate();
        const [search, setSearch] = useSearchParamState('catalogSearch', '');
        const [searchInputValue, setSearchInputValue] = useState(search);
        const [activePage, setActivePage] = useSearchParamState('catalogPage', '1');
        const [itemsPerPage, setItemsPerPage] = useSearchParamState('catalogPageSize', DEFAULT_PAGE_SIZE.toString());
        const [showFilters, setShowFilters] = useBooleanSearchParamState('catalogFiltersOpen', false);
        const [automationFilter, setAutomationFilter] = useArraySearchParamState('catalogAutomationFilter');
        const [statusFilter, setStatusFilter] = useArraySearchParamState('catalogStatusFilter');
        const [mailboxFilter, setMailboxFilter] = useArraySearchParamState('catalogMailboxFilter');
        const parsedPage = parseInt(activePage || '1', 10);
        const parsedPageSize = parseInt(itemsPerPage || DEFAULT_PAGE_SIZE.toString(), 10);
        const hasActiveFilters = automationFilter.length > 0 || statusFilter.length > 0 || mailboxFilter.length > 0;

        const propagateSearch = (value: string) => {
            setSearch(value);
            setActivePage('1');
        };
        const debouncedPropagateSearch = useDebounce(propagateSearch, 500);

        const { rows, totalCount, loading, agentOptions, mailboxOptions, refetch } = useCategoryCatalog(
            parsedPage,
            parsedPageSize,
            { search, automationFilter, statusFilter, mailboxFilter }
        );
        const isEmpty = !loading && rows.length === 0 && !search && !hasActiveFilters;

        useEffect(() => {
            onEmptyChange?.(isEmpty);
        }, [isEmpty, onEmptyChange]);

        const [openMenuId, setOpenMenuId] = useState<string | null>(null);
        const [modalOpen, setModalOpen] = useState(false);
        const [selectedMailboxId, setSelectedMailboxId] = useState('');

        const { categoryToDelete, setCategoryToDelete, pendingActionId, deleting, handleTogglePause, handleDelete } =
            useCategoryOperations<CategoryCatalogRow>(refetch);

        const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, { fetchPolicy: 'network-only' });
        const { data: selectedIntegrationData, refetch: refetchSelectedIntegration } = useQuery(
            GET_SMART_INBOX_INTEGRATION,
            {
                variables: { id: selectedMailboxId },
                skip: !selectedMailboxId,
                fetchPolicy: 'cache-and-network',
            }
        );
        const destinationFolders = selectedIntegrationData?.smartInboxIntegration?.destinationFolders || [];

        const { createCustomCategory, addRecommendedCategories, creating, addingRecommended } = useCategoryCreate(
            selectedMailboxId,
            channelData
        );
        const form = useForm<CategoryFormData>({ defaultValues: CATEGORY_FORM_DEFAULT_VALUES, mode: 'onChange' });

        const goToCategoryDetail = (row: CategoryCatalogRow) =>
            navigate(url(CATEGORY_CATALOG_DETAIL_PATH, { mailboxId: row.mailboxId, categoryId: row.id }));

        const openCreateModal = () => {
            form.reset(CATEGORY_FORM_DEFAULT_VALUES);
            setSelectedMailboxId('');
            setModalOpen(true);
        };

        const clearFilters = () => {
            setSearchInputValue('');
            setSearch('');
            setAutomationFilter([]);
            setStatusFilter([]);
            setMailboxFilter([]);
            setActivePage('1');
        };

        React.useImperativeHandle(ref, () => ({ openCreateModal }));

        const openCreatedCategory = async (createdIds: string[] | null) => {
            if (!createdIds) return;
            setModalOpen(false);
            if (createdIds.length === 1) {
                navigate(
                    url(CATEGORY_CATALOG_DETAIL_PATH, { mailboxId: selectedMailboxId, categoryId: createdIds[0] })
                );
                return;
            }
            await Promise.all([refetch(), refetchSelectedIntegration()]);
        };

        const handleSubmit = async (formData: CategoryFormData) =>
            openCreatedCategory(await createCustomCategory(formData));

        const handleCreateRecommended = async (recommended: RecommendedCategory[]) =>
            openCreatedCategory(await addRecommendedCategories(recommended));

        return (
            <>
                <Page.Header className="py-0 justify-between" primary={false}>
                    <div className="flex h-full">{tabsNav}</div>
                    {!isEmpty && (
                        <div className="flex items-center gap-2 py-2.5">
                            <SearchField
                                placeholder={t('detail.categoryCatalogTab.searchPlaceholder')}
                                value={searchInputValue}
                                onValueChange={(value: string) => {
                                    setSearchInputValue(value);
                                    debouncedPropagateSearch(value);
                                }}
                            />
                            <button
                                type="button"
                                className={classnames(
                                    'flex items-center gap-2 h-8 px-3 rounded cursor-pointer text-sm font-medium text-content-strong hover:bg-surface-strong',
                                    (showFilters || hasActiveFilters) &&
                                        '!bg-surface-brand-medium-default !text-content-brand-strong'
                                )}
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <FilterListIcon />
                                {t('detail.categoryCatalogTab.filters.toggleLabel')}
                                {hasActiveFilters && (
                                    <span>
                                        ({automationFilter.length + statusFilter.length + mailboxFilter.length})
                                    </span>
                                )}
                            </button>
                        </div>
                    )}
                </Page.Header>
                {showFilters && !isEmpty && (
                    <div className="flex items-center gap-3 px-7 py-2 border-b border-solid border-line-weak bg-surface-medium flex-none">
                        <Filter
                            label={t('detail.categoryCatalogTab.filters.automation.label')}
                            allLabel={t('detail.categoryCatalogTab.filters.automation.all')}
                            options={agentOptions}
                            selected={automationFilter}
                            onSelectedChange={(v) => {
                                setAutomationFilter(v);
                                setActivePage('1');
                            }}
                            multiple
                        />
                        <Filter
                            label={t('detail.categoryCatalogTab.filters.status.label')}
                            allLabel={t('detail.categoryCatalogTab.filters.status.all')}
                            options={[
                                { value: 'active', label: t('detail.categoryCatalogTab.statusActive') },
                                { value: 'paused', label: t('detail.categoryCatalogTab.statusPaused') },
                            ]}
                            selected={statusFilter}
                            onSelectedChange={(v) => {
                                setStatusFilter(v);
                                setActivePage('1');
                            }}
                            multiple
                        />
                        <Filter
                            label={t('detail.categoryCatalogTab.filters.mailbox.label')}
                            allLabel={t('detail.categoryCatalogTab.filters.mailbox.all')}
                            options={mailboxOptions.map((m) => ({ value: m.id, label: m.address }))}
                            selected={mailboxFilter}
                            onSelectedChange={(v) => {
                                setMailboxFilter(v);
                                setActivePage('1');
                            }}
                            multiple
                            showSearch
                        />
                    </div>
                )}

                <Page.Content lowered className="flex flex-col gap-4">
                    {loading && rows.length === 0 ? (
                        <div className="flex items-center justify-center py-16">
                            <SpinnerIcon spin className="text-3xl text-content-weak" />
                        </div>
                    ) : isEmpty ? (
                        <EmptyStage
                            size={88}
                            title={t('detail.categoryCatalogTab.emptyTitle')}
                            description={t('detail.categoryCatalogTab.emptyDescription')}
                            action={
                                <Button variant="primary" onClick={openCreateModal}>
                                    {t('detail.categoriesTab.addCategory')}
                                </Button>
                            }
                        />
                    ) : rows.length === 0 ? (
                        <EmptyStage
                            size={88}
                            pose={SAD}
                            title={t('detail.categoryCatalogTab.noResultsTitle')}
                            description={t('detail.categoryCatalogTab.noResultsDescription')}
                            action={
                                <Button variant="outline" onClick={clearFilters}>
                                    {t('detail.categoryCatalogTab.clearFilters')}
                                </Button>
                            }
                        />
                    ) : (
                        <TableScrollWrapper>
                            <Table className="w-full table-auto">
                                <Table.Head sticky>
                                    <Table.Row>
                                        <Table.HeadCell>
                                            {t('detail.categoryCatalogTab.columns.category')}
                                        </Table.HeadCell>
                                        <Table.HeadCell>
                                            {t('detail.categoryCatalogTab.columns.automation')}
                                        </Table.HeadCell>
                                        <Table.HeadCell>
                                            {t('detail.categoryCatalogTab.columns.outlookTag')}
                                        </Table.HeadCell>
                                        <Table.HeadCell>
                                            {t('detail.categoryCatalogTab.columns.mailbox')}
                                        </Table.HeadCell>
                                        <Table.HeadCell>{t('detail.categoryCatalogTab.columns.status')}</Table.HeadCell>
                                        <Table.HeadCell sticky="right" />
                                    </Table.Row>
                                </Table.Head>
                                <Table.Body>
                                    {rows.map((row) => (
                                        <Table.Row
                                            key={row.id}
                                            className="cursor-pointer hover:bg-surface-weak transition-colors group [&>td]:group-hover:bg-surface-strong"
                                            onClick={() => goToCategoryDetail(row)}
                                        >
                                            <Table.Cell>
                                                <span className="font-medium text-content-strong">{row.name}</span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                {row.automationName ? (
                                                    <span className="flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-surface-agent-weak text-content-agent-default flex-none w-fit">
                                                        <AgentIcon className="text-[10px]" />
                                                        {row.automationName}
                                                    </span>
                                                ) : (
                                                    <span className="text-content-weak">
                                                        {t('detail.categoryCatalogTab.automationNotAdded')}
                                                    </span>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {row.outlookTagName ? (
                                                    <span
                                                        className="text-xs font-medium px-1.5 py-0.5 rounded"
                                                        style={{
                                                            backgroundColor: row.outlookTagHex,
                                                            color: row.outlookTagTextHex,
                                                        }}
                                                    >
                                                        {row.outlookTagName}
                                                    </span>
                                                ) : (
                                                    <span className="text-content-weak">
                                                        {t('detail.categoryCatalogTab.automationNotAdded')}
                                                    </span>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="whitespace-nowrap">{row.mailboxAddress}</span>
                                            </Table.Cell>
                                            <Table.Cell className="whitespace-nowrap">
                                                <span
                                                    className={classnames(
                                                        'text-content-strong font-medium text-sm items-center border-[1px] px-2 py-0.5 rounded',
                                                        row.enabled
                                                            ? 'text-content-brand-medium border-line-success-weak bg-surface-success-weak'
                                                            : 'text-content-warning-default border-line-warning-weak bg-surface-warning-weak'
                                                    )}
                                                >
                                                    <DotIcon
                                                        className={classnames(
                                                            'text-[10px] pr-2 pb-[1px]',
                                                            row.enabled
                                                                ? 'text-content-success-weak'
                                                                : 'text-content-warning-weak'
                                                        )}
                                                    />
                                                    {row.enabled
                                                        ? t('detail.categoryCatalogTab.statusActive')
                                                        : t('detail.categoryCatalogTab.statusPaused')}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell sticky="right" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex justify-end">
                                                    <DropdownMenu
                                                        open={openMenuId === row.id}
                                                        onOpenChange={(open) => setOpenMenuId(open ? row.id : null)}
                                                    >
                                                        <DropdownMenu.Trigger asChild>
                                                            <button
                                                                className="flex items-center justify-center w-8 h-8 rounded hover:bg-surface-strong-hover cursor-pointer disabled:opacity-50 disabled:pointer-events-none"
                                                                disabled={pendingActionId === row.id}
                                                            >
                                                                <EllipsisIcon />
                                                            </button>
                                                        </DropdownMenu.Trigger>
                                                        <DropdownMenu.Content align="end">
                                                            <DropdownMenu.Item onClick={() => handleTogglePause(row)}>
                                                                {row.enabled
                                                                    ? t('detail.categoriesTab.actionPause')
                                                                    : t('detail.categoriesTab.actionResume')}
                                                            </DropdownMenu.Item>
                                                            <DropdownMenu.Item onClick={() => goToCategoryDetail(row)}>
                                                                {t('detail.categoriesTab.actionEdit')}
                                                            </DropdownMenu.Item>
                                                            <DropdownMenu.Separator />
                                                            <DropdownMenu.Item
                                                                danger
                                                                onClick={() => setCategoryToDelete(row)}
                                                            >
                                                                {t('detail.categoriesTab.actionDelete')}
                                                            </DropdownMenu.Item>
                                                        </DropdownMenu.Content>
                                                    </DropdownMenu>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </TableScrollWrapper>
                    )}

                    {totalCount > 0 && (
                        <TablePagination
                            totalCount={totalCount}
                            activePage={parsedPage}
                            itemsPerPage={parsedPageSize}
                            setActivePage={(page) => setActivePage(page.toString())}
                            setItemsPerPage={(size) => setItemsPerPage(size.toString())}
                        />
                    )}

                    {modalOpen && (
                        <CreateCustomCategoryModal
                            form={form}
                            destinationFolders={destinationFolders}
                            isLoading={creating || addingRecommended}
                            integrationId={selectedMailboxId}
                            channels={channelData}
                            mailboxOptions={mailboxOptions}
                            onMailboxChange={setSelectedMailboxId}
                            onClose={() => setModalOpen(false)}
                            onSubmit={handleSubmit}
                            onCreateRecommended={handleCreateRecommended}
                            title={t('detail.categoriesTab.createTitle')}
                            submitButtonLabel={t('detail.categoriesTab.createSubmit')}
                            quickCreate
                        />
                    )}

                    {categoryToDelete && (
                        <ConfirmModal
                            onConfirm={handleDelete}
                            onCancel={() => setCategoryToDelete(null)}
                            title={t('detail.categoriesTab.deleteConfirmTitle', { name: categoryToDelete.name })}
                            labelConfirm={t('detail.categoriesTab.actionDelete')}
                            confirmDisabled={deleting}
                            danger
                        >
                            {t('detail.categoriesTab.deleteConfirmMessage', { name: categoryToDelete.name })}
                        </ConfirmModal>
                    )}
                </Page.Content>
            </>
        );
    }
);

export default CategoryCatalogTab;
